<template>
  <v-card class="br-data-table-card" elevation="0" outlined>
    <div v-if="title || $slots.header || searchable" class="br-data-table-card__header">
      <div v-if="title" class="br-data-table-card__title-row">
        <v-icon v-if="icon" left :color="iconColor" size="22">{{ icon }}</v-icon>
        <h2 class="br-data-table-card__title">{{ title }}</h2>
        <v-chip
          v-if="showCount && itemCount !== null"
          x-small
          class="br-chip-count br-data-table-card__count"
        >
          {{ itemCount }}
        </v-chip>
      </div>
      <slot name="header" />
      <v-text-field
        v-if="searchable"
        :value="search"
        :label="searchLabel"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
        class="br-data-table-card__search br-form-field"
        @input="$emit('update:search', $event)"
      />
    </div>

    <v-data-table
      v-bind="$attrs"
      :headers="headers"
      :items="items"
      :loading="loading"
      :search="searchable ? search : $attrs.search"
      :class="['br-data-table', tableClass]"
      v-on="$listeners"
    >
      <template v-for="(_, slotName) in $scopedSlots" v-slot:[slotName]="slotData">
        <slot :name="slotName" v-bind="slotData" />
      </template>
    </v-data-table>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

type TableHeader = {
  text: string;
  value: string;
  sortable?: boolean;
  align?: string;
  width?: string;
};

export default defineComponent({
  name: "DataTableCard",

  inheritAttrs: false,

  props: {
    title: { type: String, default: "" },
    icon: { type: String, default: "" },
    iconColor: { type: String, default: "primary" },
    headers: { type: Array as PropType<TableHeader[]>, default: () => [] },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    search: { type: String, default: "" },
    searchLabel: { type: String, default: "Buscar" },
    showCount: { type: Boolean, default: false },
    tableClass: { type: String, default: "" },
  },

  computed: {
    itemCount(): number | null {
      return Array.isArray(this.items) ? this.items.length : null;
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.br-data-table-card {
  border-radius: $br-radius-xl !important;
  border-color: $br-border-soft !important;
  background: $br-bg-elevated !important;
  box-shadow: $br-shadow-md !important;
  overflow: hidden;
}

.br-data-table-card__header {
  padding: $br-space-4 $br-space-4 $br-space-2;
  display: flex;
  flex-direction: column;
  gap: $br-space-3;
  overflow: visible;
  background: linear-gradient(180deg, $br-bg-subtle, $br-bg-elevated);
  border-bottom: 1px solid $br-border-soft;
}

.br-data-table-card__title-row {
  display: flex;
  align-items: center;
  gap: $br-space-2;
}

.br-data-table-card__title {
  font-size: 1.05rem;
  font-weight: 800;
  color: $br-ink-soft;
  margin: 0;
  letter-spacing: -0.1px;
}

.br-data-table-card__count {
  font-weight: 800 !important;
}

.br-data-table-card__search {
  width: 100%;
  max-width: 100%;

  @media (min-width: 600px) {
    max-width: 480px;
  }
}

.br-data-table-card ::v-deep .v-data-table__wrapper {
  border-radius: 0 0 $br-radius-xl $br-radius-xl;
  overflow-x: auto;
}

.br-data-table-card ::v-deep .v-data-table {
  background: transparent !important;
}

.br-data-table-card ::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  font-size: $br-font-size-md;
  color: $br-ink-soft;
  border-color: $br-border-soft !important;
}

.br-data-table-card ::v-deep .v-data-table__empty-wrapper {
  padding: $br-space-8 $br-space-4;
  color: $br-muted;
}
</style>
